import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useBeforeUnload, useBlocker, useNavigate, useParams } from 'react-router-dom';
import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { Control, Controller, FieldErrors, useController, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { faLinkSimple, faLinkSimpleSlash, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { SettingsCard } from '../../core/components/SettingsFormCard.tsx';
import Button from '../../core/components/Button.tsx';
import DecimalField, { DECIMAL_VALIDATION_REGEXP } from '../../core/components/Fields/DecimalField.tsx';
import Loader from '../../core/components/Loader.tsx';
import Modal from '../../core/components/Modal.tsx';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../core/components/DropdownMenu.tsx';
import Radio from '../../core/components/Radio.tsx';
import SwitchRow from '../../core/components/SwitchRow.tsx';
import { Alert } from '../../core/components/Alert.tsx';
import SupportOnlyBadge from '../../customer/components/SupportOnlyBadge.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { url } from '../../core/utils/link.ts';
import FieldsConfig from './FieldsConfig.tsx';
import { useGetFieldLabel } from '../llmExtractorFields.ts';
import { GET_MATCHING_CONFIG, UPDATE_MATCHING_CONFIG } from '../queries.ts';
import { DEFAULT_LINE_ITEM_TYPE, mapDocumentTypeToGqlDocumentType } from '../constants.ts';
import { GROUP_SEPARATOR } from '../pages/Assistance/useDocumentConfig.ts';

const AddIcon = withIcon(faPlus);
const LinkIcon = withIcon(faLinkSimple);
const UnlinkIcon = withIcon(faLinkSimpleSlash);

// "NO" exists in the backend enum, but in the UI a field that should not be matched is simply not configured
const APPLY_MATCHING_OPTIONS = ['IF_IN_MASTER_DATA', 'ALWAYS', 'ONLY_FOR_ASSIGNMENT'] as const;
const BLOCK_AUTOMATION_OPTIONS = ['WHEN_VALUES_DIFFER', 'WHEN_VALUES_DIFFER_AND_NOT_EMPTY'] as const;
const COMPARISON_TYPE_OPTIONS = [
    'EXACT_MATCH',
    'THRESHOLD_DECIMAL',
    'THRESHOLD_WITH_PERCENTAGE',
    'THRESHOLD_ALL_DAYS',
    'THRESHOLD_WEEKDAYS',
    'STRING_DISTANCE',
] as const;
const CLEANING_METHOD_OPTIONS = [
    'IGNORE_NUMBER_FORMAT',
    'IGNORE_CASE',
    'REMOVE_SPACES',
    'REMOVE_COMMAS',
    'FULL_STRING_CLEANING',
] as const;

type ApplyMatching = (typeof APPLY_MATCHING_OPTIONS)[number] | 'NO';
type BlockAutomation = (typeof BLOCK_AUTOMATION_OPTIONS)[number];
type ComparisonType = (typeof COMPARISON_TYPE_OPTIONS)[number];
type CleaningMethod = (typeof CLEANING_METHOD_OPTIONS)[number];

// Comparison types that need a threshold to work at all (STRING_DISTANCE falls back to a default)
const THRESHOLD_REQUIRED_COMPARISON_TYPES: ReadonlySet<string> = new Set([
    'THRESHOLD_DECIMAL',
    'THRESHOLD_WITH_PERCENTAGE',
    'THRESHOLD_ALL_DAYS',
    'THRESHOLD_WEEKDAYS',
]);
// Comparison types that accept separate lower and upper bounds
const ASYMMETRIC_THRESHOLD_COMPARISON_TYPES: ReadonlySet<string> = new Set([
    'THRESHOLD_WITH_PERCENTAGE',
    'THRESHOLD_ALL_DAYS',
    'THRESHOLD_WEEKDAYS',
]);
const DAY_COMPARISON_TYPES: ReadonlySet<string> = new Set(['THRESHOLD_ALL_DAYS', 'THRESHOLD_WEEKDAYS']);
// An absolute tolerance has no sensible default without knowing the field, so it is not offered for pairing
const ASSIGNMENT_ONLY_EXCLUDED_COMPARISON_TYPES: ReadonlySet<string> = new Set(['THRESHOLD_DECIMAL']);
// Dates are normalized by the backend; cleaning would make them unparsable
const DATE_VALUE_TYPES: ReadonlySet<string> = new Set(['date', 'datetime']);

interface SupportedMatchingField {
    name: string;
    isLineItem: boolean;
    isEnabledForChannel: boolean;
    defaultComparisonType: ComparisonType;
    // Comparisons the field's value type can handle
    allowedComparisonTypes: ComparisonType[];
    valueType: string | null;
    // Derived client-side from matchingConfig.documentNumberField: the field whose value looks up the master data document
    isDocumentNumber: boolean;
}

// The query returns the document number once on the parent, not a flag per supported field
type SupportedMatchingFieldQuery = Omit<SupportedMatchingField, 'isDocumentNumber'>;

interface MatchingFieldConfig {
    name: string;
    isLineItem: boolean;
    isEnabledForChannel: boolean;
    applyMatching: ApplyMatching;
    blockAutomation: BlockAutomation | 'NO';
    comparisonType: ComparisonType | null;
    thresholdValue: number | null;
    upperThresholdValue: number | null;
    maxAllowedDeviation: number | null;
    cleaningMethods: CleaningMethod[];
}

interface MatchingConfigQueryResult {
    id: string | null;
    isPersisted: boolean;
    isMatchingEnabled: boolean;
    // Other channels using the same config row
    sharedChannelNames: string[];
    fields: MatchingFieldConfig[];
    supportedFields: SupportedMatchingFieldQuery[];
    // Name of the supported field that looks up the master data document
    documentNumberField: string | null;
}

interface MatchingConfigData {
    matchingConfig: MatchingConfigQueryResult;
}

interface MatchingFieldFormValue {
    name: string;
    isLineItem: boolean;
    isEnabledForChannel: boolean;
    isDocumentNumber: boolean;
    applyMatching: ApplyMatching;
    // null means automation is never blocked by this field
    blockAutomation: BlockAutomation | null;
    comparisonType: ComparisonType | '';
    thresholdValue: string;
    hasAsymmetricThreshold: boolean;
    upperThresholdValue: string;
    maxAllowedDeviation: string;
    cleaningMethods: CleaningMethod[];
}

interface MatchingConfigFormValues {
    fields: MatchingFieldFormValue[];
}

const decimalToString = (value: number | null | undefined) =>
    value === null || value === undefined ? '' : String(value);

const parseDecimal = (value: string): number | null => {
    if (!value) return null;
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isNaN(parsed) ? null : parsed;
};

// Text similarity is stored as 0-1 but edited as a percentage
const isPercentageOfOne = (comparisonType: ComparisonType | '' | null) => comparisonType === 'STRING_DISTANCE';
const showsPercentSign = (comparisonType: ComparisonType | '' | null) =>
    comparisonType === 'THRESHOLD_WITH_PERCENTAGE' || isPercentageOfOne(comparisonType);
const toDisplayThreshold = (value: number | null, comparisonType: ComparisonType | null) =>
    decimalToString(value !== null && isPercentageOfOne(comparisonType) ? Math.round(value * 10000) / 100 : value);
const fromDisplayThreshold = (value: string, comparisonType: ComparisonType | '' | null) => {
    const parsed = parseDecimal(value);
    return parsed !== null && isPercentageOfOne(comparisonType) ? parsed / 100 : parsed;
};

const toFormValue = (field: MatchingFieldConfig, isDocumentNumber: boolean): MatchingFieldFormValue => ({
    name: field.name,
    isLineItem: field.isLineItem,
    isEnabledForChannel: field.isEnabledForChannel,
    isDocumentNumber,
    applyMatching: field.applyMatching,
    blockAutomation: field.blockAutomation === 'NO' ? null : field.blockAutomation,
    comparisonType: field.comparisonType || '',
    thresholdValue: toDisplayThreshold(field.thresholdValue, field.comparisonType),
    hasAsymmetricThreshold: field.upperThresholdValue !== null && field.upperThresholdValue !== undefined,
    upperThresholdValue: toDisplayThreshold(field.upperThresholdValue, field.comparisonType),
    maxAllowedDeviation: decimalToString(field.maxAllowedDeviation),
    cleaningMethods: field.cleaningMethods || [],
});

// Mirrors the backend defaults for a freshly configured field
const newFormValue = (supportedField: SupportedMatchingField): MatchingFieldFormValue => ({
    name: supportedField.name,
    isLineItem: supportedField.isLineItem,
    isEnabledForChannel: supportedField.isEnabledForChannel,
    isDocumentNumber: supportedField.isDocumentNumber,
    applyMatching: 'IF_IN_MASTER_DATA',
    blockAutomation: 'WHEN_VALUES_DIFFER',
    comparisonType: supportedField.defaultComparisonType,
    thresholdValue: '',
    hasAsymmetricThreshold: false,
    upperThresholdValue: '',
    maxAllowedDeviation: '',
    cleaningMethods: [],
});

const toMutationInput = (value: MatchingFieldFormValue) => {
    const comparisonType = value.comparisonType || null;
    const isAssignmentOnly = value.applyMatching === 'ONLY_FOR_ASSIGNMENT';
    const isAsymmetric =
        value.hasAsymmetricThreshold && ASYMMETRIC_THRESHOLD_COMPARISON_TYPES.has(comparisonType || '');
    return {
        name: value.name,
        applyMatching: value.applyMatching,
        // assignment-only fields never validate, so they cannot block automation either
        blockAutomation: isAssignmentOnly || !value.blockAutomation ? 'NO' : value.blockAutomation,
        comparisonType,
        thresholdValue:
            comparisonType === 'EXACT_MATCH' ? null : fromDisplayThreshold(value.thresholdValue, comparisonType),
        upperThresholdValue: isAsymmetric ? fromDisplayThreshold(value.upperThresholdValue, comparisonType) : null,
        maxAllowedDeviation:
            comparisonType === 'THRESHOLD_WITH_PERCENTAGE' ? parseDecimal(value.maxAllowedDeviation) : null,
        cleaningMethods: value.cleaningMethods,
    };
};

interface SortableField {
    name: string;
    isLineItem: boolean;
    isDocumentNumber: boolean;
}

// The document number first, then header fields, then line items, each alphabetically by label
const compareFields =
    (getLabel: (name: string, isLineItem: boolean) => string) => (a: SortableField, b: SortableField) => {
        if (a.isDocumentNumber !== b.isDocumentNumber) return a.isDocumentNumber ? -1 : 1;
        if (a.isLineItem !== b.isLineItem) return a.isLineItem ? 1 : -1;
        return getLabel(a.name, a.isLineItem).localeCompare(getLabel(b.name, b.isLineItem));
    };

const formValuesOf = (
    matchingConfig: MatchingConfigQueryResult,
    compare: (a: SortableField, b: SortableField) => number
): MatchingConfigFormValues => {
    const documentNumberName = matchingConfig.documentNumberField;
    // Fields not enabled on the channel are ignored by matching and preserved by the backend on save, so they stay
    // out of the form (summarised under the list); the document number stays listed with a warning
    const fields = matchingConfig.fields
        .filter(
            (field) => (field.isEnabledForChannel || field.name === documentNumberName) && field.applyMatching !== 'NO'
        )
        .map((field) => toFormValue(field, field.name === documentNumberName))
        .toSorted(compare);
    return { fields };
};

type MatchingFieldErrorPath = 'comparisonType' | 'thresholdValue' | 'upperThresholdValue' | 'maxAllowedDeviation';

// Translation keys of the settings that can't be saved; the form's resolver, as RHF skips rules of unmounted fields
const fieldErrorsOf = (
    value: MatchingFieldFormValue,
    supportedField: SupportedMatchingField | undefined
): Partial<Record<MatchingFieldErrorPath, string>> => {
    // the document number is looked up, not compared
    if (value.isDocumentNumber) return {};
    const { comparisonType } = value;
    const isAssignmentOnly = value.applyMatching === 'ONLY_FOR_ASSIGNMENT';
    const comparisonOptions = (supportedField?.allowedComparisonTypes || COMPARISON_TYPE_OPTIONS).filter(
        (option) => !isAssignmentOnly || !ASSIGNMENT_ONLY_EXCLUDED_COMPARISON_TYPES.has(option)
    );
    const thresholdErrorOf = (input: string, isRequired: boolean) => {
        if (!input) return isRequired ? 'configPage.matching.form.thresholdRequired' : undefined;
        if (!DECIMAL_VALIDATION_REGEXP.test(input)) return 'configPage.matching.form.invalidNumber';
        const parsed = parseDecimal(input);
        if (isPercentageOfOne(comparisonType) && parsed !== null && (parsed < 0 || parsed > 100)) {
            return 'configPage.matching.form.percentageOutOfRange';
        }
        return undefined;
    };
    const showsThreshold = !isAssignmentOnly && !!comparisonType && comparisonType !== 'EXACT_MATCH';
    const isAsymmetric = value.hasAsymmetricThreshold && ASYMMETRIC_THRESHOLD_COMPARISON_TYPES.has(comparisonType);
    return {
        comparisonType: !comparisonType
            ? 'configPage.matching.form.comparisonTypeRequired'
            : !comparisonOptions.includes(comparisonType)
              ? 'configPage.matching.form.comparisonTypeNotAllowed'
              : undefined,
        thresholdValue: showsThreshold
            ? thresholdErrorOf(value.thresholdValue, THRESHOLD_REQUIRED_COMPARISON_TYPES.has(comparisonType))
            : undefined,
        upperThresholdValue:
            showsThreshold && isAsymmetric ? thresholdErrorOf(value.upperThresholdValue, true) : undefined,
        maxAllowedDeviation:
            comparisonType === 'THRESHOLD_WITH_PERCENTAGE' &&
            !!value.maxAllowedDeviation &&
            !DECIMAL_VALIDATION_REGEXP.test(value.maxAllowedDeviation)
                ? 'configPage.matching.form.invalidNumber'
                : undefined,
    };
};

interface MatchingConfigProps {
    documentType: string;
    channelId: string;
    configPath: string;
}

const MatchingConfig = ({ documentType, channelId, configPath }: MatchingConfigProps) => {
    const { t } = useTranslation('config');
    const gqlDocumentType = mapDocumentTypeToGqlDocumentType[documentType];
    // "offer" or "order": the kind of master data record the channel matches against
    const masterDataDocument = t(
        `configPage.matching.form.masterDataDocuments.${gqlDocumentType === 'ORDER' ? 'offer' : 'order'}`
    );
    const { data, error, refetch } = useQuery<MatchingConfigData>(GET_MATCHING_CONFIG, {
        variables: { documentType: gqlDocumentType, channelId },
        fetchPolicy: 'no-cache',
    });

    if (data?.matchingConfig.isMatchingEnabled) {
        return (
            <MatchingConfigEditor
                matchingConfig={data.matchingConfig}
                refetch={refetch}
                gqlDocumentType={gqlDocumentType}
                channelId={channelId}
                configPath={configPath}
                masterDataDocument={masterDataDocument}
            />
        );
    }

    return (
        <SettingsCard
            title={t('configPage.matching.title')}
            description={data && t('configPage.matching.description', { masterDataDocument })}
            className="relative"
        >
            <SupportOnlyBadge />
            {data ? (
                <Alert severity="info">
                    <span>
                        {t('configPage.matching.matchingDisabledNotice')}{' '}
                        <Link to={url(configPath, { channelId, tab: 'document' })} className="underline">
                            {t('configPage.matching.matchingDisabledLink')}
                        </Link>
                    </span>
                </Alert>
            ) : error ? (
                <span className="text-sm text-content-danger-strong">{error.message}</span>
            ) : (
                <Loader />
            )}
        </SettingsCard>
    );
};

interface MatchingConfigEditorProps {
    matchingConfig: MatchingConfigQueryResult;
    refetch: () => Promise<ApolloQueryResult<MatchingConfigData>>;
    gqlDocumentType: string;
    channelId: string;
    configPath: string;
    masterDataDocument: string;
}

// the fields compared against the master data document in a list; each opens its own page (config/matching/<name>)
const MatchingConfigEditor = ({
    matchingConfig,
    refetch,
    gqlDocumentType,
    channelId,
    configPath,
    masterDataDocument,
}: MatchingConfigEditorProps) => {
    const { t } = useTranslation('config');
    const { i18n } = useTranslation();
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const { tab, configId } = useParams();
    const getFieldLabel = useGetFieldLabel();
    // Header and line-item fields can share a name with different labels (quote_number is "Offer reference" on
    // the header), so header fields are resolved from the header namespace first
    const getLabel = (name: string, isLineItem: boolean) =>
        !isLineItem && i18n.exists(`assistance:headerView.fieldNames.${name}`)
            ? i18n.t(`assistance:headerView.fieldNames.${name}`)
            : getFieldLabel(isLineItem ? `${DEFAULT_LINE_ITEM_TYPE}${GROUP_SEPARATOR}${name}` : name);
    const getGroupLabel = (isLineItem: boolean) =>
        t(`configPage.matching.form.fieldGroups.${isLineItem ? 'lineItemFields' : 'headerFields'}`);
    const compare = compareFields(getLabel);

    const documentNumberName = matchingConfig.documentNumberField;
    const supportedFields: SupportedMatchingField[] = matchingConfig.supportedFields.map((field) => ({
        ...field,
        isDocumentNumber: field.name === documentNumberName,
    }));
    const supportedByName = new Map(supportedFields.map((field) => [field.name, field]));

    const [updateMatchingConfig] = useMutation(UPDATE_MATCHING_CONFIG);
    const form = useForm<MatchingConfigFormValues>({
        defaultValues: formValuesOf(matchingConfig, compare),
        mode: 'onTouched',
        resolver: (values) => {
            const errors = values.fields.map((value) => {
                const messageKeys = Object.entries(fieldErrorsOf(value, supportedByName.get(value.name))).filter(
                    ([, key]) => key
                );
                if (!messageKeys.length) return undefined;
                return Object.fromEntries(
                    messageKeys.map(([path, key]) => [path, { type: 'validate', message: t(key) }])
                );
            });
            return errors.some(Boolean)
                ? { values: {}, errors: { fields: errors } as FieldErrors<MatchingConfigFormValues> }
                : { values, errors: {} };
        },
    });
    const fieldArray = useFieldArray({ control: form.control, name: 'fields', keyName: 'key' });
    const configuredFields = form.watch('fields');
    const { isDirty } = form.formState;
    // form data waiting for the "this channel or all channels" decision on a shared config
    const [pendingSubmit, setPendingSubmit] = useState<MatchingConfigFormValues | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const toField = (name: string | null) => url(configPath, { channelId, tab, configId: name });

    // the list and the field pages share one form, so only leaving the tab drops unsaved changes
    const listPath = toField(null);
    const blocker = useBlocker(
        ({ nextLocation }) =>
            form.formState.isDirty &&
            nextLocation.pathname !== listPath &&
            !nextLocation.pathname.startsWith(`${listPath}/`)
    );
    useBeforeUnload((event) => {
        if (form.formState.isDirty) event.preventDefault();
    });

    const configuredNames = new Set(configuredFields.map((field) => field.name));
    const savedNames = new Set(
        matchingConfig.fields.filter((field) => field.applyMatching !== 'NO').map((field) => field.name)
    );
    const addableFields = supportedFields
        .filter((field) => field.isEnabledForChannel && !configuredNames.has(field.name))
        .toSorted(compare);

    // a field added here opens as a draft on its page, so its settings are checked before the first save
    const handleAddField = (name: string) => {
        const supportedField = supportedByName.get(name);
        if (!supportedField || configuredNames.has(name)) return;
        const newValue = newFormValue(supportedField);
        const insertIndex = configuredFields.findIndex((field) => compare(newValue, field) < 0);
        fieldArray.insert(insertIndex === -1 ? configuredFields.length : insertIndex, newValue);
        navigate(toField(name));
    };

    const sharedChannelNames = matchingConfig.sharedChannelNames;

    const saveConfig = async (formData: MatchingConfigFormValues, applyToAllChannels: boolean) => {
        setPendingSubmit(null);
        setIsSaving(true);
        try {
            await updateMatchingConfig({
                variables: {
                    documentType: gqlDocumentType,
                    channelId,
                    fields: formData.fields.map(toMutationInput),
                    applyToAllChannels,
                },
            });
        } catch (e) {
            console.error(e);
            const message = e instanceof Error ? e.message : t('configPage.matching.updateError');
            publishToast({ description: message, status: 'error' });
            setIsSaving(false);
            return;
        }
        publishToast({ description: t('configPage.matching.updateSuccess'), status: 'success' });
        try {
            const result = await refetch();
            form.reset(formValuesOf(result.data.matchingConfig, compare));
        } catch (e) {
            // saved all the same, only reloading it failed
            console.error(e);
            form.reset(formData);
        }
        setIsSaving(false);
    };

    // every Save sends the whole list: the backend replaces it
    const submit = (formData: MatchingConfigFormValues) =>
        sharedChannelNames.length > 0 ? setPendingSubmit(formData) : saveConfig(formData, false);
    const save = form.handleSubmit(
        submit,
        // a setting that isn't valid is on a field's page, so that page opens
        (errors) => {
            const index = (errors.fields || []).findIndex(Boolean);
            if (index >= 0) navigate(toField(configuredFields[index].name));
        }
    );

    const openIndex = configId ? configuredFields.findIndex((field) => field.name === configId) : -1;
    const openField = openIndex >= 0 ? configuredFields[openIndex] : null;
    const isDraft = (name: string) => !savedNames.has(name);

    const cancel = () => {
        form.reset();
        if (openField && isDraft(openField.name)) navigate(toField(null));
    };

    const listCrumb = { label: t('configPage.matching.title'), onClick: () => navigate(toField(null)) };
    const modals = (
        <>
            {pendingSubmit && (
                <Modal
                    title={t('configPage.matching.sharedConfigDialog.title')}
                    onClose={() => setPendingSubmit(null)}
                    buttons={[
                        <Button key="cancel" type="button" onClick={() => setPendingSubmit(null)}>
                            {t('configPage.matching.sharedConfigDialog.cancel')}
                        </Button>,
                        <Button key="this" type="button" onClick={() => saveConfig(pendingSubmit, false)}>
                            {t('configPage.matching.sharedConfigDialog.saveForThisChannel')}
                        </Button>,
                        <Button
                            key="all"
                            type="button"
                            variant="primary"
                            onClick={() => saveConfig(pendingSubmit, true)}
                        >
                            {t('configPage.matching.sharedConfigDialog.saveForAllChannels', {
                                count: sharedChannelNames.length + 1,
                            })}
                        </Button>,
                    ]}
                >
                    <p>
                        {t('configPage.matching.sharedConfigDialog.text', {
                            count: sharedChannelNames.length + 1,
                            channels: sharedChannelNames.join(', '),
                        })}
                    </p>
                </Modal>
            )}
            {blocker.state === 'blocked' && (
                <ConfirmModal
                    title={t('configPage.document.discardTitle')}
                    labelConfirm={t('configPage.document.discardConfirm')}
                    onConfirm={() => {
                        form.reset();
                        blocker.proceed();
                    }}
                    onCancel={() => blocker.reset()}
                    danger
                >
                    {t('configPage.matching.leaveMessage')}
                </ConfirmModal>
            )}
        </>
    );

    if (configId) {
        if (!openField) {
            return (
                <FieldsConfig.Page
                    className="relative"
                    parents={[listCrumb]}
                    current={t('configPage.document.fieldNotInUse')}
                >
                    <SupportOnlyBadge />
                    {modals}
                </FieldsConfig.Page>
            );
        }

        const fieldLabel = getLabel(openField.name, openField.isLineItem);
        const supportedField = supportedByName.get(openField.name);
        return (
            <FieldsConfig.Page
                className="relative"
                parents={[listCrumb]}
                current={fieldLabel}
                title={fieldLabel}
                // the group under the title: a header field and a line item can have the same label
                subtitle={[getGroupLabel(openField.isLineItem), isDraft(openField.name) && t('configPage.notSavedYet')]
                    .filter(Boolean)
                    .join(' · ')}
                actions={
                    // Remove saves at once, so it waits for other changes to be saved or cancelled (a draft too)
                    !openField.isDocumentNumber &&
                    !isDirty && (
                        <FieldsConfig.RemoveMenu
                            fieldLabel={fieldLabel}
                            message={t('configPage.document.removeFieldMessage')}
                            onRemove={() => {
                                const { fields } = form.getValues();
                                navigate(toField(null));
                                submit({ fields: fields.filter((field) => field.name !== openField.name) });
                            }}
                        />
                    )
                }
            >
                <SupportOnlyBadge />
                <MatchingFieldForm
                    key={openField.name}
                    index={openIndex}
                    control={form.control}
                    allowedComparisonTypes={supportedField?.allowedComparisonTypes || COMPARISON_TYPE_OPTIONS}
                    isDocumentNumber={openField.isDocumentNumber}
                    allowsCleaning={!DATE_VALUE_TYPES.has(supportedField?.valueType || '')}
                    headerFieldsUrl={url(configPath, { channelId, tab: 'fields' })}
                    masterDataDocument={masterDataDocument}
                    fieldLabel={fieldLabel}
                />
                {isDirty && <FieldsConfig.SaveBar onCancel={cancel} onSave={save} isSaving={isSaving} />}
                {modals}
            </FieldsConfig.Page>
        );
    }

    const ignoredFields = matchingConfig.fields.filter(
        (field) => !field.isEnabledForChannel && field.name !== documentNumberName && field.applyMatching !== 'NO'
    );

    // "Percentage deviation −0 % / +2 % · Blocks automation": what is checked, without opening the field
    const amountOf = (value: string, comparisonType: ComparisonType) => {
        if (showsPercentSign(comparisonType)) return t('configPage.matching.list.percent', { value });
        if (DAY_COMPARISON_TYPES.has(comparisonType)) {
            return t('configPage.matching.list.days', { count: parseDecimal(value) ?? 0 });
        }
        return value;
    };
    const summaryOf = (field: MatchingFieldFormValue) => {
        const comparisonType = field.comparisonType || null;
        const comparison = comparisonType && t(`configPage.matching.form.comparisonTypeOptions.${comparisonType}`);
        if (field.applyMatching === 'ONLY_FOR_ASSIGNMENT') {
            return [t('configPage.matching.list.assignmentOnly'), comparison];
        }
        const isAsymmetric =
            field.hasAsymmetricThreshold && ASYMMETRIC_THRESHOLD_COMPARISON_TYPES.has(comparisonType || '');
        let amount: string | null = null;
        if (comparisonType && comparisonType !== 'EXACT_MATCH' && field.thresholdValue) {
            if (comparisonType === 'STRING_DISTANCE') {
                amount = t('configPage.matching.list.minimum', {
                    value: amountOf(field.thresholdValue, comparisonType),
                });
            } else if (isAsymmetric && field.upperThresholdValue) {
                amount = t('configPage.matching.list.splitDeviation', {
                    lower: amountOf(field.thresholdValue, comparisonType),
                    upper: amountOf(field.upperThresholdValue, comparisonType),
                });
            } else {
                amount = t('configPage.matching.list.deviation', {
                    value: amountOf(field.thresholdValue, comparisonType),
                });
            }
        }
        return [
            [comparison, amount].filter(Boolean).join(' '),
            t(
                field.blockAutomation
                    ? 'configPage.matching.list.blocksAutomation'
                    : 'configPage.matching.list.doesNotBlockAutomation'
            ),
        ];
    };
    const rowOf = (field: MatchingFieldFormValue) => ({
        name: field.name,
        label: getLabel(field.name, field.isLineItem),
        note: [
            getGroupLabel(field.isLineItem),
            ...(field.isDocumentNumber
                ? [
                      t(
                          field.blockAutomation
                              ? 'configPage.matching.list.documentNumberBlocks'
                              : 'configPage.matching.list.documentNumberDoesNotBlock',
                          { masterDataDocument }
                      ),
                  ]
                : summaryOf(field)),
            isDraft(field.name) && t('configPage.notSavedYet'),
        ]
            .filter(Boolean)
            .join(' · '),
    });
    const documentNumberRows = configuredFields.filter((field) => field.isDocumentNumber).map(rowOf);
    const comparedRows = configuredFields.filter((field) => !field.isDocumentNumber).map(rowOf);

    return (
        <SettingsCard
            className="relative pb-10"
            title={t('configPage.matching.title')}
            description={t('configPage.matching.description', { masterDataDocument })}
        >
            <SupportOnlyBadge />
            {!matchingConfig.isPersisted && (
                <Alert severity="info">{t('configPage.matching.defaultConfigNotice')}</Alert>
            )}
            {sharedChannelNames.length > 0 && (
                <Alert severity="info">
                    {t('configPage.matching.sharedConfigNotice', {
                        count: sharedChannelNames.length,
                        channels: sharedChannelNames.join(', '),
                    })}
                </Alert>
            )}
            {documentNumberRows.length > 0 && (
                <FieldsConfig.Section
                    title={t('configPage.matching.documentNumberTitle', { masterDataDocument })}
                    description={t('configPage.matching.documentNumberSectionDescription', { masterDataDocument })}
                >
                    <FieldsConfig.List rows={documentNumberRows} toRow={toField} />
                </FieldsConfig.Section>
            )}
            <FieldsConfig.Section title={t('configPage.matching.comparedFieldsTitle')}>
                {comparedRows.length > 0 ? (
                    <FieldsConfig.List
                        rows={comparedRows}
                        toRow={toField}
                        onRemove={(name) =>
                            fieldArray.remove(configuredFields.findIndex((field) => field.name === name))
                        }
                    />
                ) : (
                    <p className="text-sm text-content-medium">{t('configPage.matching.noFields')}</p>
                )}
                {addableFields.length > 0 && (
                    <div className="flex">
                        <DropdownMenu>
                            <DropdownMenu.Trigger asChild>
                                <Button type="button" variant="outline">
                                    <AddIcon /> {t('configPage.document.addFieldLabel')}
                                </Button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content align="start" className="min-w-56">
                                {[false, true].map((isLineItem) => {
                                    const groupFields = addableFields.filter(
                                        (field) => field.isLineItem === isLineItem
                                    );
                                    if (!groupFields.length) return null;
                                    return (
                                        <DropdownMenu.Group key={String(isLineItem)}>
                                            <DropdownMenu.Label>
                                                {t(
                                                    `configPage.document.fieldSections.${isLineItem ? 'lineItems' : 'header'}`
                                                )}
                                            </DropdownMenu.Label>
                                            {groupFields.map((field) => (
                                                <DropdownMenu.Item
                                                    key={field.name}
                                                    onSelect={() => handleAddField(field.name)}
                                                >
                                                    {getLabel(field.name, field.isLineItem)}
                                                </DropdownMenu.Item>
                                            ))}
                                        </DropdownMenu.Group>
                                    );
                                })}
                            </DropdownMenu.Content>
                        </DropdownMenu>
                    </div>
                )}
            </FieldsConfig.Section>
            {ignoredFields.length > 0 && (
                <p className="text-sm text-content-medium">
                    {t('configPage.matching.ignoredFieldsNotice', {
                        count: ignoredFields.length,
                        fields: ignoredFields.map((field) => getLabel(field.name, field.isLineItem)).join(', '),
                    })}
                </p>
            )}
            {isDirty && <FieldsConfig.SaveBar onCancel={cancel} onSave={save} isSaving={isSaving} />}
            {modals}
        </SettingsCard>
    );
};

// a choice in a card: the radio, its label and what it does
interface RadioRowProps {
    value: string;
    label: string;
    description?: string;
}

const RadioRow = ({ value, label, description = undefined }: RadioRowProps) => (
    <label className="group flex cursor-pointer items-start gap-3 px-4 py-3 text-sm transition-colors hover:bg-surface-medium">
        <Radio value={value} />
        <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-content-strong">{label}</span>
            {description && <span className="text-xs text-content-medium">{description}</span>}
        </span>
    </label>
);

interface MatchingFieldFormProps {
    index: number;
    control: Control<MatchingConfigFormValues>;
    allowedComparisonTypes: readonly ComparisonType[];
    // The document number is looked up, not compared: only blocking and the lookup cleaning apply
    isDocumentNumber: boolean;
    allowsCleaning: boolean;
    headerFieldsUrl: string;
    // "offer" or "order": the kind of master data record the channel matches against
    masterDataDocument: string;
    fieldLabel: string;
}

const MatchingFieldForm = ({
    index,
    control,
    allowedComparisonTypes,
    isDocumentNumber,
    allowsCleaning,
    headerFieldsUrl,
    masterDataDocument,
    fieldLabel,
}: MatchingFieldFormProps) => {
    const { t } = useTranslation('config');
    const value = useWatch({ control, name: `fields.${index}` });

    const comparisonType = value.comparisonType;
    const isAssignmentOnly = value.applyMatching === 'ONLY_FOR_ASSIGNMENT';
    const useOptions = APPLY_MATCHING_OPTIONS.filter(
        (option) =>
            // pairing only applies to line items; for a header field it would mean doing nothing
            (value.isLineItem || option !== 'ONLY_FOR_ASSIGNMENT') &&
            // ALWAYS is legacy: kept visible only where it is already set
            (option !== 'ALWAYS' || value.applyMatching === 'ALWAYS')
    );
    // a single choice (header fields) is noise
    const showsUse = !isDocumentNumber && useOptions.length > 1;
    // assignment-only fields keep their comparison, but thresholds and cleaning are managed by the backend
    const showsComparison = !isDocumentNumber;
    const showsThreshold = showsComparison && !isAssignmentOnly && !!comparisonType && comparisonType !== 'EXACT_MATCH';
    const showsCleaning = !isAssignmentOnly && allowsCleaning;
    const comparisonOptions = isAssignmentOnly
        ? allowedComparisonTypes.filter((option) => !ASSIGNMENT_ONLY_EXCLUDED_COMPARISON_TYPES.has(option))
        : allowedComparisonTypes;
    const comparison = useController({ control, name: `fields.${index}.comparisonType` });

    // the field label as used mid-sentence ("order number", "offer reference")
    const fieldName = fieldLabel.charAt(0).toLowerCase() + fieldLabel.slice(1);

    return (
        <>
            {isDocumentNumber && (
                <Alert severity="info">
                    {t('configPage.matching.form.documentNumberLocked', { masterDataDocument })}
                </Alert>
            )}
            {isDocumentNumber && !value.isEnabledForChannel && (
                <Alert severity="warning">
                    <span>
                        {t('configPage.matching.form.documentNumberNotEnabled', { masterDataDocument, fieldName })}{' '}
                        <Link to={headerFieldsUrl} className="underline">
                            {t('configPage.matching.form.documentNumberNotEnabledLink')}
                        </Link>
                    </span>
                </Alert>
            )}

            {showsUse && (
                <FieldsConfig.Section title={t('configPage.matching.form.useTitle')}>
                    <FieldsConfig.Card>
                        <Controller
                            name={`fields.${index}.applyMatching`}
                            control={control}
                            render={({ field }) => (
                                <Radio.Group
                                    value={field.value}
                                    onValueChange={(mode) => {
                                        field.onChange(mode as ApplyMatching);
                                        if (
                                            mode === 'ONLY_FOR_ASSIGNMENT' &&
                                            ASSIGNMENT_ONLY_EXCLUDED_COMPARISON_TYPES.has(comparison.field.value)
                                        ) {
                                            comparison.field.onChange('EXACT_MATCH');
                                        }
                                    }}
                                    className="gap-0"
                                >
                                    <FieldsConfig.Rows>
                                        {useOptions.map((option) => (
                                            <RadioRow
                                                key={option}
                                                value={option}
                                                label={t(`configPage.matching.form.applyMatchingOptions.${option}`)}
                                                description={t(
                                                    `configPage.matching.form.applyMatchingDescriptions.${option}`,
                                                    { masterDataDocument }
                                                )}
                                            />
                                        ))}
                                    </FieldsConfig.Rows>
                                </Radio.Group>
                            )}
                        />
                    </FieldsConfig.Card>
                </FieldsConfig.Section>
            )}

            {showsComparison && (
                <FieldsConfig.Section
                    title={t(
                        isAssignmentOnly
                            ? 'configPage.matching.form.pairingTitle'
                            : 'configPage.matching.form.comparisonType'
                    )}
                    description={t(
                        isAssignmentOnly
                            ? 'configPage.matching.form.pairingDescription'
                            : 'configPage.matching.form.comparisonDescription',
                        { masterDataDocument }
                    )}
                >
                    <FieldsConfig.Card>
                        <div className="flex flex-col">
                            <Radio.Group
                                value={comparison.field.value}
                                onValueChange={comparison.field.onChange}
                                className="gap-0"
                            >
                                <FieldsConfig.Rows>
                                    {comparisonOptions.map((option) => (
                                        <RadioRow
                                            key={option}
                                            value={option}
                                            label={t(`configPage.matching.form.comparisonTypeOptions.${option}`)}
                                        />
                                    ))}
                                </FieldsConfig.Rows>
                            </Radio.Group>
                            {comparison.fieldState.error?.message && (
                                <span className="px-4 pb-3 text-xs text-content-danger-weak">
                                    {comparison.fieldState.error.message}
                                </span>
                            )}
                        </div>
                        {showsThreshold && (
                            <div className="px-4 py-3">
                                <ThresholdField index={index} control={control} comparisonType={comparisonType} />
                            </div>
                        )}
                        {comparisonType === 'THRESHOLD_WITH_PERCENTAGE' && (
                            <Controller
                                name={`fields.${index}.maxAllowedDeviation`}
                                control={control}
                                render={({ field, fieldState }) => (
                                    <FieldsConfig.OptionsFormField
                                        label={t('configPage.matching.form.maxAllowedDeviation')}
                                        helpText={t('configPage.matching.form.maxAllowedDeviationDescription')}
                                        error={fieldState.error}
                                        valueType="string"
                                        nullable={false}
                                        defaultValue=""
                                        component={DecimalField}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        onBlur={field.onBlur}
                                    />
                                )}
                            />
                        )}
                    </FieldsConfig.Card>
                </FieldsConfig.Section>
            )}

            {showsCleaning && (
                <FieldsConfig.Section
                    title={t(
                        isDocumentNumber
                            ? 'configPage.matching.form.cleaningMethodsDocumentNumber'
                            : 'configPage.matching.form.cleaningMethods'
                    )}
                    description={t(
                        isDocumentNumber
                            ? 'configPage.matching.form.cleaningMethodsDocumentNumberDescription'
                            : 'configPage.matching.form.cleaningMethodsDescription',
                        { fieldName }
                    )}
                >
                    <Controller
                        name={`fields.${index}.cleaningMethods`}
                        control={control}
                        render={({ field }) => {
                            // full text cleaning applies every other method, so those show as on and locked
                            const hasFullCleaning = field.value.includes('FULL_STRING_CLEANING');
                            return (
                                <FieldsConfig.Card>
                                    {CLEANING_METHOD_OPTIONS.map((method) => (
                                        <SwitchRow
                                            key={method}
                                            className="rounded-none px-4 py-3"
                                            label={t(`configPage.matching.form.cleaningMethodOptions.${method}`)}
                                            value={hasFullCleaning || field.value.includes(method)}
                                            disabled={hasFullCleaning && method !== 'FULL_STRING_CLEANING'}
                                            onValueChange={(isOn) =>
                                                field.onChange(
                                                    isOn
                                                        ? [...field.value, method]
                                                        : field.value.filter((other) => other !== method)
                                                )
                                            }
                                        />
                                    ))}
                                </FieldsConfig.Card>
                            );
                        }}
                    />
                </FieldsConfig.Section>
            )}

            {!isAssignmentOnly && (
                <FieldsConfig.Section title={t('configPage.matching.form.automationTitle')}>
                    <Controller
                        name={`fields.${index}.blockAutomation`}
                        control={control}
                        render={({ field }) => (
                            <FieldsConfig.Card>
                                <SwitchRow
                                    className="rounded-none px-4 py-3"
                                    label={t('configPage.matching.form.blockAutomation')}
                                    description={t(
                                        `configPage.matching.form.blockAutomation${isDocumentNumber ? 'DocumentNumber' : ''}${field.value ? '' : 'Off'}Description`,
                                        { masterDataDocument, fieldName }
                                    )}
                                    value={!!field.value}
                                    onValueChange={(isOn) => field.onChange(isOn ? 'WHEN_VALUES_DIFFER' : null)}
                                />
                                {field.value && (
                                    <Radio.Group value={field.value} onValueChange={field.onChange} className="gap-0">
                                        <FieldsConfig.Rows>
                                            {BLOCK_AUTOMATION_OPTIONS.map((option) => (
                                                <RadioRow
                                                    key={option}
                                                    value={option}
                                                    label={t(
                                                        `configPage.matching.form.${isDocumentNumber ? 'blockAutomationDocumentNumberOptions' : 'blockAutomationOptions'}.${option}`,
                                                        { masterDataDocument, fieldName }
                                                    )}
                                                />
                                            ))}
                                        </FieldsConfig.Rows>
                                    </Radio.Group>
                                )}
                            </FieldsConfig.Card>
                        )}
                    />
                </FieldsConfig.Section>
            )}
        </>
    );
};

// One deviation limit, or separate limits below/above the master data value (like linked/unlinked padding
// controls in design tools)
interface ThresholdFieldProps {
    index: number;
    control: Control<MatchingConfigFormValues>;
    comparisonType: ComparisonType;
}

const ThresholdField = ({ index, control, comparisonType }: ThresholdFieldProps) => {
    const { t } = useTranslation('config');
    const isRequired = THRESHOLD_REQUIRED_COMPARISON_TYPES.has(comparisonType);
    const allowsAsymmetric = ASYMMETRIC_THRESHOLD_COMPARISON_TYPES.has(comparisonType);

    const asymmetric = useController({ control, name: `fields.${index}.hasAsymmetricThreshold` });
    const isAsymmetric = allowsAsymmetric && asymmetric.field.value;
    const lower = useController({ control, name: `fields.${index}.thresholdValue` });
    const upper = useController({ control, name: `fields.${index}.upperThresholdValue` });
    const error = lower.fieldState.error || (isAsymmetric ? upper.fieldState.error : undefined);

    const unit = showsPercentSign(comparisonType)
        ? '%'
        : DAY_COMPARISON_TYPES.has(comparisonType)
          ? t('configPage.matching.form.days')
          : null;
    const controls = unit ? <span className="px-2 text-content-weak">{unit}</span> : undefined;
    const inputClassName = (hasError: boolean) => classnames(hasError && 'border-line-danger-default');

    return (
        <div className="flex flex-col gap-2">
            <div className="text-content-strong text-sm flex justify-between items-center">
                <span>
                    {isPercentageOfOne(comparisonType)
                        ? t('configPage.matching.form.similarityThreshold')
                        : t('configPage.matching.form.thresholdValue')}{' '}
                    {isRequired && <span className="text-content-danger-strong">*</span>}
                </span>
                {allowsAsymmetric && (
                    <Button
                        type="button"
                        className="flex items-center gap-1.5"
                        active={isAsymmetric}
                        aria-pressed={isAsymmetric}
                        title={
                            isAsymmetric
                                ? t('configPage.matching.form.symmetricThreshold')
                                : t('configPage.matching.form.asymmetricThreshold')
                        }
                        onClick={() => asymmetric.field.onChange(!isAsymmetric)}
                    >
                        {isAsymmetric ? <UnlinkIcon /> : <LinkIcon />}
                        {t('configPage.matching.form.splitThresholds')}
                    </Button>
                )}
            </div>

            {isAsymmetric ? (
                <div className="grid grid-cols-2 gap-4">
                    <label className="flex flex-col gap-1">
                        <span className="text-xs text-content-medium">
                            − {t('configPage.matching.form.belowLabel')}
                        </span>
                        <DecimalField
                            className={inputClassName(!!lower.fieldState.error)}
                            value={lower.field.value}
                            onValueChange={lower.field.onChange}
                            onBlur={lower.field.onBlur}
                            required={isRequired}
                            controls={controls}
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-xs text-content-medium">
                            + {t('configPage.matching.form.aboveLabel')}
                        </span>
                        <DecimalField
                            className={inputClassName(!!upper.fieldState.error)}
                            value={upper.field.value}
                            onValueChange={upper.field.onChange}
                            onBlur={upper.field.onBlur}
                            required
                            controls={controls}
                        />
                    </label>
                </div>
            ) : (
                <DecimalField
                    className={inputClassName(!!lower.fieldState.error)}
                    value={lower.field.value}
                    onValueChange={lower.field.onChange}
                    onBlur={lower.field.onBlur}
                    required={isRequired}
                    controls={controls}
                />
            )}

            {!error?.message && (
                <span className="text-content-medium text-xs">
                    {t(`configPage.matching.form.thresholdHints.${comparisonType}`)}
                </span>
            )}
            {error?.message && <span className="text-content-danger-weak text-xs">{error.message}</span>}
        </div>
    );
};

export default MatchingConfig;
